import { FaUser, FaGripVertical, FaExclamationTriangle } from "react-icons/fa";
import { HeroIconByName } from "./HeroIconByName";

interface SalesFunnelItemCardProps {
  item: any;
  onClick: () => void;
  cardLayout?: any;
  fieldDefinitions?: any[];
}

const FONT_SIZE_CLASS: Record<string, string> = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
};

function widthToStyle(width: string): React.CSSProperties {
  switch (width) {
    case "1/2": return { flexBasis: "50%" };
    case "1/3": return { flexBasis: "33.33%" };
    case "2/3": return { flexBasis: "66.66%" };
    case "1/4": return { flexBasis: "25%" };
    case "3/4": return { flexBasis: "75%" };
    case "auto": return { flexShrink: 1, flexGrow: 0 };
    default: return { flex: 1 };
  }
}

function getMissingRequiredFields(item: any, fieldDefinitions?: any[]): string[] {
  if (!fieldDefinitions) return [];
  const requiredFields = fieldDefinitions.filter((fd: any) => fd.required && fd.type !== "COMPUTED");
  if (requiredFields.length === 0) return [];
  const itemFieldIds = new Set(item.fieldValues?.map((fv: any) => String(fv.fieldDefinition.id)) || []);
  return requiredFields
    .filter((fd: any) => {
      const hasValue = itemFieldIds.has(String(fd.id));
      if (!hasValue) return true;
      const fv = item.fieldValues.find((v: any) => String(v.fieldDefinition.id) === String(fd.id));
      return fv?.value === null || fv?.value === undefined || fv?.value === "";
    })
    .map((fd: any) => fd.label);
}

export default function SalesFunnelItemCard({ item, onClick, cardLayout, fieldDefinitions }: SalesFunnelItemCardProps) {
  const missingFields = getMissingRequiredFields(item, fieldDefinitions);
  const hasMissingFields = missingFields.length > 0;

  if (cardLayout) {
    return <LayoutCard item={item} onClick={onClick} cardLayout={cardLayout} hasMissingFields={hasMissingFields} missingFields={missingFields} />;
  }
  return <DefaultCard item={item} onClick={onClick} hasMissingFields={hasMissingFields} missingFields={missingFields} />;
}

function DefaultCard({ item, onClick, hasMissingFields, missingFields }: { item: any; onClick: () => void; hasMissingFields: boolean; missingFields: string[] }) {
  return (
    <div
      className={`bg-white rounded-md shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow group ${hasMissingFields ? "border-2 border-red-500" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-start">
        <FaGripVertical className="text-gray-300 mr-2 mt-1 opacity-0 group-hover:opacity-100 cursor-grab" />
        <div className="flex-1 min-w-0">
          {hasMissingFields && (
            <div className="flex items-center gap-1 text-xs text-red-600 mb-1">
              <FaExclamationTriangle className="w-3 h-3" />
              <span>Missing: {missingFields.join(", ")}</span>
            </div>
          )}
          <h4 className="text-sm font-medium text-gray-900 truncate">{item.title}</h4>

          {item.fieldValues?.filter((fv: any) => !fv.fieldDefinition.options?.hideFromCard).length > 0 && (
            <div className="mt-2 space-y-1">
              {item.fieldValues
                .filter((fv: any) => !fv.fieldDefinition.options?.hideFromCard)
                .map((fv: any) => (
                  <div key={fv.id} className="text-xs text-gray-500 flex items-center gap-1">
                    <HeroIconByName name={fv.fieldDefinition.options?.icon} className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="font-medium">{fv.fieldDefinition.label}:</span>{" "}
                    {formatFieldValue(fv.value, fv.fieldDefinition.type, fv.fieldDefinition.options?.currency)}
                  </div>
                ))}
            </div>
          )}

          <div className="mt-2 flex items-center justify-between">
            <div className="flex -space-x-2">
              {item.assignedUsers?.map((au: any) => (
                <img
                  key={au.id}
                  src={au.user.picture || "/dummy-picture.jpg"}
                  alt={au.user.name}
                  title={au.user.name}
                  className="w-6 h-6 rounded-full border-2 border-white"
                />
              ))}
              {item.assignedUsers?.length > 3 && (
                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                  +{item.assignedUsers.length - 3}
                </div>
              )}
              {(!item.assignedUsers || item.assignedUsers.length === 0) && (
                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                  <FaUser className="text-gray-400 text-xs" />
                </div>
              )}
            </div>

            <div className="flex items-center">
              <img
                src={item.author?.picture || "/dummy-picture.jpg"}
                alt={item.author?.name}
                title={`Created by ${item.author?.name}`}
                className="w-5 h-5 rounded-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LayoutCard({ item, onClick, cardLayout, hasMissingFields, missingFields }: { item: any; onClick: () => void; cardLayout: any; hasMissingFields: boolean; missingFields: string[] }) {
  const fieldMap = new Map<string, any>();
  item.fieldValues?.forEach((fv: any) => {
    fieldMap.set(String(fv.fieldDefinition.id), fv);
  });

  function renderField(fieldConfig: any) {
    const { fieldId, width, fontSize, bold, italic, showLabel, showIcon } = fieldConfig;
    const sizeClass = FONT_SIZE_CLASS[fontSize || "sm"] || "text-sm";
    const style = widthToStyle(width || "full");

    if (fieldId === "__title") {
      return (
        <div key="__title" style={style} className={`${sizeClass} ${bold ? "font-bold" : "font-medium"} text-gray-900 truncate ${italic ? "italic" : ""}`}>
          {item.title}
        </div>
      );
    }

    if (fieldId === "__assignees") {
      return (
        <div key="__assignees" style={style} className="flex items-center">
          <div className="flex -space-x-2">
            {item.assignedUsers?.map((au: any) => (
              <img
                key={au.id}
                src={au.user.picture || "/dummy-picture.jpg"}
                alt={au.user.name}
                title={au.user.name}
                className="w-5 h-5 rounded-full border-2 border-white"
              />
            ))}
            {item.assignedUsers?.length > 3 && (
              <div className="w-5 h-5 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-[10px] text-gray-600">
                +{item.assignedUsers.length - 3}
              </div>
            )}
            {(!item.assignedUsers || item.assignedUsers.length === 0) && (
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                <FaUser className="text-gray-400 text-[10px]" />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (fieldId === "__author") {
      return (
        <div key="__author" style={style} className="flex items-center">
          <img
            src={item.author?.picture || "/dummy-picture.jpg"}
            alt={item.author?.name}
            title={`Created by ${item.author?.name}`}
            className="w-5 h-5 rounded-full"
          />
        </div>
      );
    }

    // Regular field
    const fv = fieldMap.get(fieldId);
    if (!fv) return null;

    const icon = showIcon !== false ? fv.fieldDefinition.options?.icon : undefined;
    const label = showLabel !== false ? fv.fieldDefinition.label : null;
    const currency = fv.fieldDefinition.options?.currency;

    return (
      <div key={fieldId} style={style} className={`${sizeClass} text-gray-600 flex items-center gap-1 min-w-0 ${bold ? "font-bold" : ""} ${italic ? "italic" : ""}`}>
        {icon && <HeroIconByName name={icon} className="w-3 h-3 flex-shrink-0 text-gray-400" />}
        {label && <span className="font-medium flex-shrink-0">{label}:</span>}
        <span className="truncate">{formatFieldValue(fv.value, fv.fieldDefinition.type, currency)}</span>
      </div>
    );
  }

  function renderZone(zone: string) {
    const zoneData = cardLayout[zone];
    if (!zoneData?.rows || zoneData.rows.length === 0) return null;

    return (
      <div className="space-y-1">
        {zoneData.rows.map((row: any, rowIdx: number) => (
          <div key={rowIdx} className="flex gap-2 items-start">
            {row.fields.map((fieldConfig: any) => renderField(fieldConfig))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-md shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow group ${hasMissingFields ? "border-2 border-red-500" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-start">
        <FaGripVertical className="text-gray-300 mr-2 mt-1 opacity-0 group-hover:opacity-100 cursor-grab" />
        <div className="flex-1 min-w-0 space-y-2">
          {hasMissingFields && (
            <div className="flex items-center gap-1 text-xs text-red-600">
              <FaExclamationTriangle className="w-3 h-3 flex-shrink-0" />
              <span>Missing: {missingFields.join(", ")}</span>
            </div>
          )}
          {renderZone("header")}
          {renderZone("body")}
          {renderZone("footer")}
        </div>
      </div>
    </div>
  );
}

function formatFieldValue(value: any, type: string, currency?: string): string {
  if (value === null || value === undefined) return "-";

  // Handle computed field errors
  if (typeof value === "object" && value?.error) {
    return `⚠️ ${value.error}`;
  }

  switch (type) {
    case "BOOLEAN":
      return value ? "Yes" : "No";
    case "DATE":
      return new Date(value).toLocaleDateString();
    case "NUMBER": {
      const num = typeof value === "number" ? value : parseFloat(value);
      const formatted = isNaN(num) ? String(value) : num.toLocaleString();
      return currency ? `${currency}\u00a0${formatted}` : formatted;
    }
    case "SELECT":
      return value?.label || value || "-";
    case "MULTI_SELECT":
      if (Array.isArray(value)) {
        return value.map((v) => v?.label || v).join(", ");
      }
      return "-";
    case "COMPUTED":
      // Computed fields store the result directly
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return String(value);
    case "CARD":
      // Card fields store { itemId, boardId, title }
      if (typeof value === "object" && value?.title) {
        return `🔗 ${value.title}`;
      }
      return "-";
    default:
      return String(value);
  }
}
